import { PageOrientation, RequestStatus, defaults, entities, getAPIMethod } from 'typed/api';
import { ExtractAction, NetworkAction, action, entityActionSet, networkAction } from './utils';
import {
    BusinessReviewSettings, StrategyActionListContentSettings, StrategyContentSettings, StrategyFilter, StrategyOrganizationStructureFilter, StrategyPlannerFilter
} from 'typed/components/strategy/utils';
import { download } from 'typed/utils';

type AddStrategicProgramStatusUpdate = NetworkAction<
'ADD_STRATEGIC_PROGRAM_STATUS_UPDATE',
{ parentID: number; entityID: number; entity: entities.StrategicProgramStatusUpdateReport; },
number
>;

export const
    businessModelCanvasAreas = entityActionSet(defaults.BusinessModelCanvasArea, {
        loadAll: networkAction('LOAD_ALL_BUSINESS_MODEL_CANVAS_AREAS', getAPIMethod('BusinessModelCanvasArea', 'loadAll')),
        edit: networkAction('EDIT_BUSINESS_MODEL_CANVAS_AREA', getAPIMethod('BusinessModelCanvasArea', 'edit'))
    }),

    businessModels = entityActionSet(defaults.BusinessModelReport, {
        loadAll: networkAction('LOAD_ALL_BUSINESS_MODELS', getAPIMethod('BusinessModel', 'loadAll')),
        add: networkAction('ADD_BUSINESS_MODEL', getAPIMethod('BusinessModel', 'add')),
        edit: networkAction('EDIT_BUSINESS_MODEL', getAPIMethod('BusinessModel', 'edit')),
        delete: networkAction('DELETE_BUSINESS_MODEL', getAPIMethod('BusinessModel', 'delete')),
        voidAll: action('VOID_ALL_BUSINESS_MODELS')
    }),

    businessModelCanvasItems = {
        loadAll: (parentID: number) => networkAction(
            'LOAD_ALL_BUSINESS_MODEL_CANVAS_ITEMS', { parentID }, getAPIMethod('BusinessModelCanvasItem', 'search'), { ModelID: parentID }, {}
        ),
        ...entityActionSet(defaults.BusinessModelReport, defaults.BusinessModelCanvasItem, {
            add: networkAction('ADD_BUSINESS_MODEL_CANVAS_ITEM', getAPIMethod('BusinessModelCanvasItem', 'add')),
            edit: networkAction('EDIT_BUSINESS_MODEL_CANVAS_ITEM', getAPIMethod('BusinessModelCanvasItem', 'edit')),
            delete: networkAction('DELETE_BUSINESS_MODEL_CANVAS_ITEM', getAPIMethod('BusinessModelCanvasItem', 'delete')),
            voidAll: action('VOID_ALL_BUSINESS_MODEL_CANVAS_ITEMS')
        })
    },

    { loadAll: loadAllBusinessModelCanvasItemTypes } = entityActionSet(defaults.BusinessModelCanvasItemType, {
        loadAll: networkAction('LOAD_ALL_BUSINESS_MODEL_CANVAS_ITEM_TYPES', getAPIMethod('BusinessModelCanvasItemType', 'loadAll'))
    }),

    { loadAll: loadAllStrategyHealthReports } = entityActionSet(defaults.StrategyHealthReport, {
        loadAll: networkAction('LOAD_ALL_STRATEGY_HEALTH_REPORTS', getAPIMethod('StrategyHealthReport', 'loadAll'))
    }),

    strategyProcesses = entityActionSet(defaults.StrategyProcessReport, {
        loadAll: networkAction('LOAD_ALL_STRATEGY_PROCESSES', getAPIMethod('StrategyProcess', 'loadAll')),
        add: networkAction('ADD_STRATEGY_PROCESS', getAPIMethod('StrategyProcess', 'add')),
        edit: networkAction('EDIT_STRATEGY_PROCESS', getAPIMethod('StrategyProcess', 'edit')),
        delete: networkAction('DELETE_STRATEGY_PROCESS', getAPIMethod('StrategyProcess', 'delete'))
    }),

    strategyProcessThemes = entityActionSet(defaults.StrategyProcessReport, defaults.StrategyProcessThemeReport, {
        addAll: networkAction('ADD_ALL_STRATEGY_PROCESS_THEMES', getAPIMethod('StrategyProcessTheme', 'addAll')),
        editAll: networkAction('EDIT_ALL_STRATEGY_PROCESS_THEMES', getAPIMethod('StrategyProcessTheme', 'editAll')),
        deleteAll: networkAction('DELETE_ALL_STRATEGY_PROCESS_THEMES', getAPIMethod('StrategyProcessTheme', 'deleteAll'))
    }),

    { loadAll: loadAllStrategyProcessSubthemeTypes } = entityActionSet(defaults.StrategyProcessSubthemeType, {
        loadAll: networkAction('LOAD_ALL_STRATEGY_PROCESS_SUBTHEME_TYPES', getAPIMethod('StrategyProcessSubthemeType', 'loadAll'))
    }),

    strategyProcessSubthemes = entityActionSet(defaults.StrategyProcessReport, defaults.StrategyProcessThemeReport, defaults.StrategyProcessSubthemeReport, {
        add: networkAction('ADD_STRATEGY_PROCESS_SUBTHEME', getAPIMethod('StrategyProcessSubtheme', 'add')),
        edit: networkAction('EDIT_STRATEGY_PROCESS_SUBTHEME', getAPIMethod('StrategyProcessSubtheme', 'edit')),
        delete: networkAction('DELETE_STRATEGY_PROCESS_SUBTHEME', getAPIMethod('StrategyProcessSubtheme', 'delete'))
    }),

    strategyProcessSubthemeLinks = entityActionSet(
        defaults.StrategyProcessReport,
        defaults.StrategyProcessThemeReport,
        defaults.StrategyProcessSubthemeReport,
        defaults.StrategyProcessSubthemeLink,
        {
            addAll: networkAction('ADD_ALL_STRATEGY_PROCESS_SUBTHEME_LINK', getAPIMethod('StrategyProcessSubthemeLink', 'addAll')),
            deleteAll: networkAction('DELETE_ALL_STRATEGY_PROCESS_SUBTHEME_LINK', getAPIMethod('StrategyProcessSubthemeLink', 'deleteAll'))
        }),

    strategyProcessPhases = entityActionSet(defaults.StrategyProcessPhaseReport, {
        loadAll: networkAction('LOAD_ALL_STRATEGY_PROCESS_PHASES', getAPIMethod('StrategyProcessPhase', 'loadAll')),
        add: networkAction('ADD_STRATEGY_PROCESS_PHASE', getAPIMethod('StrategyProcessPhase', 'add')),
        edit: networkAction('EDIT_STRATEGY_PROCESS_PHASE', getAPIMethod('StrategyProcessPhase', 'edit')),
        delete: networkAction('DELETE_STRATEGY_PROCESS_PHASE', getAPIMethod('StrategyProcessPhase', 'delete'))
    }),

    strategyProcessPhaseContents = entityActionSet(defaults.StrategyProcessPhaseReport, defaults.StrategyProcessPhaseContent, {
        add: networkAction('ADD_STRATEGY_PROCESS_PHASE_CONTENT', getAPIMethod('StrategyProcessPhaseContent', 'add')),
        edit: networkAction('EDIT_STRATEGY_PROCESS_PHASE_CONTENT', getAPIMethod('StrategyProcessPhaseContent', 'edit')),
        delete: networkAction('DELETE_STRATEGY_PROCESS_PHASE_CONTENT', getAPIMethod('StrategyProcessPhaseContent', 'delete'))
    }),

    strategyTaxonomies = entityActionSet(defaults.StrategyTaxonomy, {
        loadAll: networkAction('LOAD_ALL_STRATEGY_TAXONOMIES', getAPIMethod('StrategyTaxonomy', 'loadAll')),
        editAll: networkAction('EDIT_ALL_STRATEGY_TAXONOMIES', getAPIMethod('StrategyTaxonomy', 'editAll')),
        edit: networkAction('EDIT_STRATEGY_TAXONOMY', getAPIMethod('StrategyTaxonomy', 'edit'))
    }),
 
    strategyDescriptions = {
        ...entityActionSet(defaults.StrategyDescriptionReport, {

             load: networkAction('LOAD_STRATEGY_DESCRIPTION', getAPIMethod('StrategyDescription', 'load')),
            add: networkAction('ADD_STRATEGY_DESCRIPTION', getAPIMethod('StrategyDescription', 'add')),
            editAll: networkAction('EDIT_ALL_STRATEGY_DESCRIPTIONS', getAPIMethod('StrategyDescription', 'editAll')),
            voidAll: action('VOID_ALL_STRATEGY_DESCRIPTIONS')
        }),  

        loadAll: (options: { maxCommentsCount?: number; maxMetricsCount?: number; maxStatusUpdatesCount?: number; includeDataActuals?: number } = {}) =>
            networkAction('LOAD_ALL_STRATEGY_DESCRIPTIONS', getAPIMethod('StrategyDescription', 'loadAll'), options),


        edit: (entity: entities.StrategyDescriptionReport, sync = true, reloadAfterSync = false, updateSubStrategiesLabels = false): NetworkAction<'EDIT_STRATEGY_DESCRIPTION', void, void> =>
            async(dispatch: any) => {
                dispatch({
                    type: 'EDIT_STRATEGY_DESCRIPTION',
                    payload: { entity, entityID: entity.ID, status: RequestStatus.UNSYNCED, updateSubStrategiesLabels }
                });

                if (!sync) {
                    return undefined;
                }
                else {
                    const syncAction = await dispatch(networkAction(
                        'EDIT_STRATEGY_DESCRIPTION',
                        { entity, entityID: entity.ID, updateSubStrategiesLabels },
                        getAPIMethod('StrategyDescription', 'edit'),
                        entity,
                        { updateSubStrategiesLabels }
                    ));

                    if (reloadAfterSync) {
                        return await dispatch(strategyDescriptions.load(entity.ID));
                    }

                    return syncAction;
                }
            },
        delete: (entity: entities.StrategyDescriptionReport, sync = true, deleteSubstrategies = false): NetworkAction<'DELETE_STRATEGY_DESCRIPTION', void, void> =>
            async(dispatch: any) => {
                if (!sync) {
                    dispatch({
                        type: 'DELETE_STRATEGY_DESCRIPTION',
                        payload: { status: RequestStatus.SUCCEEDED, entity, entityID: entity.ID, response: undefined, deleteSubstrategies }
                    });
                }
                else {
                    await dispatch(networkAction(
                        'DELETE_STRATEGY_DESCRIPTION',
                        { entity, entityID: entity.ID, deleteSubstrategies },
                        getAPIMethod<entities.StrategyDescriptionReport>('StrategyDescription', 'delete'),
                        entity.ID,
                        { deleteSubstrategies }
                    ));
                }
            },
        setStatus: (strategyDescriptionID: number, statusID: number) =>
            networkAction(
                'SET_STRATEGY_DESCRIPTION_STATUS',
                { entityID: strategyDescriptionID, statusID },
                getAPIMethod('StrategyDescriptionApi', 'strategyDescriptionSetStatusGet'),
                strategyDescriptionID,
                statusID
            ),
        setGoalDefaultCustomLabel: (strategyDescriptionID: number, customLabel: string) =>
            networkAction(
                'SET_GOAL_DEFAULT_CUSTOM_LABEL',
                { entityID: strategyDescriptionID, customLabel },
                getAPIMethod('StrategyDescriptionApi', 'strategyDescriptionSetGoalDefaultCustomLabelGet'),
                strategyDescriptionID,
                customLabel
            ),
        publish: (strategyDescriptionID: number, message: string, milestones: entities.StrategicGoalMilestoneReport[], recipientUserIDs: string) =>
            networkAction(
                'PUBLISH_STRATEGY_DESCRIPTION',
                { entityID: strategyDescriptionID, message, milestones, recipientUserIDs },
                getAPIMethod<entities.StrategyDescriptionReport>('StrategyDescription', 'publish'),
                milestones,
                strategyDescriptionID,
                message,
                { recipientUserIDs }
            ),
        publishAutomatically: (strategyDescriptionID: number) =>
            networkAction(
                'AUTOMATICALLY_PUBLISH_STRATEGY',
                { entityID: strategyDescriptionID },
                getAPIMethod<entities.StrategyDescriptionReport>('StrategyDescription', 'publishAutomatically'),
                strategyDescriptionID
            ),
        copy: (strategyDescriptionID: number, options: { copySubstrategies?: boolean; } = {}) =>
            networkAction('COPY_STRATEGY_DESCRIPTION', { strategyDescriptionID, ...options }, getAPIMethod('StrategyDescription', 'copy'), strategyDescriptionID, options),
        downloadMapPdf: (imageDataUrl: string, fileName: string, pageOrientation: PageOrientation = PageOrientation.PORTRAIT): any =>
            async(dispatch: any) => {
                const response = await dispatch(networkAction(
                    'DOWNLOAD_STRATEGY_MAP_PDF',
                    { imageDataUrls: [imageDataUrl] },
                    getAPIMethod('StrategyDescriptionApi', 'strategyDescriptionGenerateStrategyMapPdfPut'),
                    [imageDataUrl],
                    { pageOrientation }
                ));

                if (response) {
                    const linkSource = `data:application/pdf;base64,${response}`;
                    download(linkSource, fileName);
                }
            },
        downloadBusinessReviewPdf: (exportSettings: entities.StrategyBusinessReviewExportSettings, fileName: string): any =>
            async(dispatch: any) => {
                const response = await dispatch(networkAction(
                    'DOWNLOAD_STRATEGY_BUSINESS_REVIEW_PDF',
                    { exportSettings },
                    getAPIMethod('StrategyDescriptionApi', 'strategyDescriptionGenerateBusinessReviewPdfPut'),
                    exportSettings
                ));
                if (response) {
                    const linkSource = `data:application/pdf;base64,${response}`;
                    download(linkSource, fileName);
                }
            },
        exportToExcel: (exportSettings: entities.StrategyExecutionExportSettings, fileName: string): any =>
            async(dispatch: any) => {
                const response = await dispatch(networkAction(
                    'DOWNLOAD_STRATEGY_MAP_EXCEL',
                    { exportSettings },
                    getAPIMethod('StrategyDescriptionApi', 'strategyDescriptionGenerateExcelPut'),
                    exportSettings
                ));

                if (response) {
                    const linkSource = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${response}`;
                    download(linkSource, fileName);
                }
            },
        exportToCustomTaspaExcel: (exportSettings: entities.StrategyExecutionExportSettings, fileName: string): any =>
            async(dispatch: any) => {
                const response = await dispatch(networkAction(
                    'EXPORT_CUSTOM_TASPA_EXCEL',
                    { exportSettings },
                    getAPIMethod('StrategyDescription', 'strategyDescriptionExportCityOfHelsinkiMilestonesToExcelFilePut'),
                    exportSettings
                ));

                if (response) {
                    const linkSource = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${response}`;
                    download(linkSource, fileName);
                }
            },
        exportToStrategy: (items: entities.StrategyExportItemSettings[] = []) =>
            networkAction(
                'EXPORT_TO_STRATEGY',
                { items },
                getAPIMethod('StrategyDescriptionApi', 'strategyDescriptionExportToStrategyPut'),
                items
            )
    },

    { loadAll: loadAllStrategyDescriptionStatuses } = entityActionSet(defaults.StrategyDescriptionStatus, {
        loadAll: networkAction('LOAD_ALL_STRATEGY_DESCRIPTION_STATUSES', getAPIMethod('StrategyDescriptionStatus', 'loadAll'))
    }),

    { loadAll: loadAllStrategyPublishingTypes } = entityActionSet(defaults.StrategyPublishingType, {
        loadAll: networkAction('LOAD_ALL_STRATEGY_PUBLISHING_TYPES', getAPIMethod('StrategyPublishingType', 'loadAll'))
    }),

    { loadAll: loadAllStrategyNamingTypes } = entityActionSet(defaults.StrategyNamingType, {
        loadAll: networkAction('LOAD_ALL_STRATEGY_NAMING_TYPES', getAPIMethod('StrategyNamingType', 'loadAll'))
    }),

    strategyDescriptionParticipators = {
        ...entityActionSet(defaults.StrategyDescriptionReport, defaults.StrategyDescriptionParticipator, {
            addAll: networkAction('ADD_ALL_STRATEGY_DESCRIPTION_PARTICIPATORS', getAPIMethod('StrategyDescriptionParticipator', 'addAll')),
            deleteAll: networkAction('DELETE_ALL_STRATEGY_DESCRIPTION_PARTICIPATORS', getAPIMethod('StrategyDescriptionParticipator', 'deleteAll'))
        }),
        invite: (userIDs: number[], strategyDescriptionID: number, invitationType: number, coverLetter: string) => networkAction(
            'INVITE_STRATEGY_DESCRIPTION_PARTICIPATORS', { parentID: strategyDescriptionID, userIDs }, getAPIMethod('StrategyDescriptionParticipatorApi', 'strategyDescriptionParticipatorInviteUsersPut'), userIDs, strategyDescriptionID, invitationType, coverLetter
        )
    },

    strategyDescriptionAdmins = {
        ...entityActionSet(defaults.StrategyDescriptionReport, defaults.StrategyDescriptionAdministrator, {
            addAll: networkAction('ADD_ALL_STRATEGY_DESCRIPTION_ADMINISTRATORS', getAPIMethod('StrategyDescriptionAdministrator', 'addAll')),
            deleteAll: networkAction('DELETE_ALL_STRATEGY_DESCRIPTION_ADMINISTRATORS', getAPIMethod('StrategyDescriptionAdministrator', 'deleteAll'))
        })
    },

    strategyMetrics = {
        ...entityActionSet(defaults.StrategyDescriptionReport, defaults.GoalMetricReport, {
            load: networkAction('LOAD_STRATEGY_METRIC', getAPIMethod('GoalMetric', 'load')),
            edit: networkAction('EDIT_STRATEGY_METRIC', getAPIMethod('GoalMetric', 'edit')),
            delete: networkAction('DELETE_STRATEGY_METRIC', getAPIMethod('GoalMetric', 'delete'))
        }),
        add: (parentID: number, entity: entities.GoalMetricReport) =>
            networkAction(
                'ADD_STRATEGY_METRIC',
                { parentID, entityID: entity.ID, entity },
                getAPIMethod<entities.GoalMetricReport>('GoalMetricApi', 'goalMetricAddAndLoadPut'),
                entity
            )
    },

    strategicGoals = {
        ...entityActionSet(defaults.StrategicGoalReport, {
             load: networkAction('LOAD_STRATEGIC_GOAL', getAPIMethod('StrategicGoal', 'load')),
            edit: networkAction('EDIT_STRATEGIC_GOAL', getAPIMethod('StrategicGoal', 'edit')),
            delete: networkAction('DELETE_STRATEGIC_GOAL', getAPIMethod('StrategicGoal', 'delete')),
            voidAll: action('VOID_ALL_STRATEGIC_GOALS')
        }),
        loadAll: (options: {  includeDataActuals?: number } = {}) =>
            networkAction('LOAD_ALL_STRATEGIC_GOALS', getAPIMethod<entities.StrategicGoalReport[]>('StrategicGoal', 'loadAll'), options),
        add: (entity: entities.StrategicGoalReport, sync: boolean, reloadAfterSync = false):
        NetworkAction<'ADD_STRATEGIC_GOAL', { entityID: number; entity: entities.StrategicGoalReport; }, number> =>
            async(dispatch: any) => {
                dispatch(action('ADD_STRATEGIC_GOAL', { entity, entityID: entity.ID, status: RequestStatus.UNSYNCED }));

                if (!sync) { return null; }

                const newID = await dispatch(networkAction(
                    'ADD_STRATEGIC_GOAL',
                    { entity, entityID: entity.ID },
                    getAPIMethod('StrategicGoalApi', 'add'),
                    entity
                ));

                if (newID && reloadAfterSync) { dispatch(strategicGoals.load(newID)); }

                return newID;
            },
        setRoadmapStartDate: (entityID: number, teamID: number | null, startDate: Date | null) =>
            action('SET_STRATEGIC_GOAL_ROADMAP_START_DATE', { parentID: entityID, teamID, startDate }),
        clearRoadmapStartDates: (entityID: number) => action('CLEAR_STRATEGIC_GOAL_ROADMAP_START_DATES', { parentID: entityID })
    },

    setGoalCustomJourneyRange = (strategicGoalID: number, startDate: Date | null, endDate: Date | null) => action('SET_GOAL_CUSTOM_JOURNEY_RANGE', { parentID: strategicGoalID, startDate, endDate }),

    criticalSuccessFactors = entityActionSet(defaults.StrategicGoalReport, defaults.StrategicGoalCriticalSuccessFactorReport, {
        add: networkAction('ADD_CRITICAL_SUCCESS_FACTOR', getAPIMethod('StrategicGoalCriticalSuccessFactor', 'add')),
        addAll: networkAction('ADD_ALL_CRITICAL_SUCCESS_FACTORS', getAPIMethod('StrategicGoalCriticalSuccessFactor', 'addAll')),
        edit: networkAction('EDIT_CRITICAL_SUCCESS_FACTOR', getAPIMethod('StrategicGoalCriticalSuccessFactor', 'edit')),
        editAll: networkAction('EDIT_ALL_CRITICAL_SUCCESS_FACTORS', getAPIMethod('StrategicGoalCriticalSuccessFactor', 'editAll')),
        delete: networkAction('DELETE_CRITICAL_SUCCESS_FACTOR', getAPIMethod('StrategicGoalCriticalSuccessFactor', 'delete')),
        deleteAll: networkAction('DELETE_ALL_CRITICAL_SUCCESS_FACTORS', getAPIMethod('StrategicGoalCriticalSuccessFactor', 'deleteAll'))
    }),

    goalMetrics = {
        ...entityActionSet(defaults.StrategicGoalReport, defaults.GoalMetricReport, {
            load: networkAction('LOAD_GOAL_METRIC', getAPIMethod('GoalMetric', 'load')),
            edit: networkAction('EDIT_GOAL_METRIC', getAPIMethod('GoalMetric', 'edit')),
            editAll: networkAction('EDIT_ALL_GOAL_METRICS', getAPIMethod('GoalMetric', 'editAll')),
            delete: networkAction('DELETE_GOAL_METRIC', getAPIMethod('GoalMetric', 'delete')),
            deleteAll: networkAction('DELETE_ALL_GOAL_METRICS', getAPIMethod('GoalMetric', 'deleteAll'))
        }),
        add: (parentID: number, entity: entities.GoalMetricReport) =>
            networkAction('ADD_GOAL_METRIC', { parentID, entityID: entity.ID, entity }, getAPIMethod<entities.GoalMetricReport>('GoalMetricApi', 'goalMetricAddAndLoadPut'), entity),
        addAll: (parentID: number, entities: entities.GoalMetricReport[]) =>
            networkAction('ADD_ALL_GOAL_METRICS', { parentID, entities, entityIDs: entities.map(({ ID }) => ID) }, getAPIMethod<entities.GoalMetricReport[]>('GoalMetricApi', 'goalMetricAddAllAndLoadPut'), entities)
    },

    aggregateGoalMetric = (entityID: number) =>
        networkAction('AGGREGATE_GOAL_METRIC', { entityID }, getAPIMethod('GoalMetric', 'aggregateMetric'), entityID),

    { loadAll: loadAllGoalMetricValueSources } = entityActionSet(defaults.GoalMetricValueSource, {
        loadAll: networkAction('LOAD_ALL_GOAL_METRIC_VALUE_SOURCES', getAPIMethod('GoalMetricValueSource', 'loadAll'))
    }),

    { loadAll: loadAllGoalMetricValueTypes } = entityActionSet(defaults.GoalMetricValueType, {
        loadAll: networkAction('LOAD_ALL_GOAL_METRIC_VALUE_TYPES', getAPIMethod('GoalMetricValueType', 'loadAll'))
    }),

    strategicGoalMilestones = {
        loadAll: (options: { maxCommentsCount?: number; maxMetricsCount?: number; maxStatusUpdatesCount?: number; includeDataActuals?: number} = {}) =>
            networkAction('LOAD_ALL_STRATEGIC_GOAL_MILESTONES', options, getAPIMethod<entities.StrategicGoalMilestoneReport[]>('StrategicGoalMilestone', 'loadAll'), options),
        loadAllQuietly: (options: { maxCommentsCount?: number; maxMetricsCount?: number; maxStatusUpdatesCount?: number; } = {}) =>
            networkAction('LOAD_ALL_STRATEGIC_GOAL_MILESTONES_QUIETLY', options, getAPIMethod<entities.StrategicGoalMilestoneReport[]>('StrategicGoalMilestone', 'loadAll'), options),
        loadMany: (entityIDs: number[], options: { maxCommentsCount?: number; maxMetricsCount?: number; maxStatusUpdatesCount?: number; } = {}) =>
            networkAction(
                'LOAD_MANY_STRATEGIC_GOAL_MILESTONES',
                { entityIDs, ...options },
                getAPIMethod<entities.StrategicGoalMilestoneReport[]>('StrategicGoalMilestone', 'loadMany'),
                entityIDs,
                options
            ),
        load: (entityID: number, options: { maxCommentsCount?: number; maxMetricsCount?: number; maxStatusUpdatesCount?: number; } = {}) =>
            networkAction(
                'LOAD_STRATEGIC_GOAL_MILESTONE',
                { entityID, ...options },
                getAPIMethod<entities.StrategicGoalMilestoneReport>('StrategicGoalMilestone', 'load'),
                entityID,
                options
            ),
        ...entityActionSet(defaults.StrategicGoalMilestoneReport, {
            add: networkAction('ADD_STRATEGIC_GOAL_MILESTONE', getAPIMethod('StrategicGoalMilestone', 'add')),
            addAll: networkAction('ADD_ALL_STRATEGIC_GOAL_MILESTONES', getAPIMethod('StrategicGoalMilestone', 'addAll')),
            editAll: networkAction('EDIT_ALL_STRATEGIC_GOAL_MILESTONES', getAPIMethod('StrategicGoalMilestone', 'editAll')),
            delete: networkAction('DELETE_STRATEGIC_GOAL_MILESTONE', getAPIMethod('StrategicGoalMilestone', 'delete')),
            voidAll: action('VOID_ALL_STRATEGIC_GOAL_MILESTONES')
        }),
        edit: (entity: entities.StrategicGoalMilestoneReport, sync: boolean, reloadAfterSync = false):
        NetworkAction<'EDIT_STRATEGIC_GOAL_MILESTONE', { entityID: number; entity: entities.StrategicGoalMilestoneReport; }, void> =>
            async(dispatch: any) => {
                dispatch({
                    type: 'EDIT_STRATEGIC_GOAL_MILESTONE',
                    payload: { status: RequestStatus.UNSYNCED, entity, entityID: entity.ID }
                });

                if (!sync) { return undefined; }

                const syncAction = networkAction(
                    'EDIT_STRATEGIC_GOAL_MILESTONE',
                    { entity, entityID: entity.ID },
                    getAPIMethod<entities.StrategicGoalMilestoneReport>('StrategicGoalMilestone', 'edit'),
                    entity
                );

                if (!reloadAfterSync) {
                    return dispatch(syncAction);
                }
                else {
                    await dispatch(syncAction);
                    return dispatch(strategicGoalMilestones.load(entity.ID));
                }
            },
        editAndUpdateProgress: (entity: entities.StrategicGoalMilestoneReport) =>
            networkAction(
                'EDIT_STRATEGIC_GOAL_MILESTONE_AND_UPDATE_PROGRESS',
                { entityID: entity.ID, entity },
                getAPIMethod<entities.StrategicGoalMilestoneReport>('StrategicGoalMilestoneApi', 'strategicGoalMilestoneEditAndUpdateProgressPut'),
                entity
            ),
        publish: (entity: entities.StrategicGoalMilestoneReport, messageFromUser: string) =>
            networkAction(
                'PUBLISH_STRATEGIC_GOAL_MILESTONE',
                { entity, entityID: entity.ID, messageFromUser },
                getAPIMethod<entities.StrategicGoalMilestoneReport>('StrategicGoalMilestone', 'publishAndSendNotifications'),
                entity,
                messageFromUser
            ),
        sendUpdateRequestNotification: (recipients: number[], coverLetter: string) =>
            networkAction(
                'SEND_STRATEGIC_GOAL_MILESTONE_UPDATE_REQUEST_NOTIFICATION',
                { recipients, coverLetter },
                getAPIMethod('StrategicGoalMilestone', 'sendUpdateRequestNotification'),
                recipients,
                coverLetter
            ),
        archive: (entity: entities.StrategicGoalMilestoneReport) =>
            networkAction(
                'ARCHIVE_STRATEGIC_GOAL_MILESTONE',
                { entity, entityID: entity.ID },
                getAPIMethod<entities.StrategicGoalMilestoneReport>('StrategicGoalMilestone', 'archive'),
                entity
            ),
        complete: (entity: entities.StrategicGoalMilestoneReport) =>
            networkAction(
                'COMPLETE_STRATEGIC_GOAL_MILESTONE',
                { entity, entityID: entity.ID },
                getAPIMethod<entities.StrategicGoalMilestoneReport>('StrategicGoalMilestone', 'complete'),
                entity
            )
    },

    setMilestoneCustomRoadmapRange = (strategicGoalMilestoneID: number, startDate: Date | null, endDate: Date | null) =>
        action('SET_MILESTONE_CUSTOM_ROADMAP_RANGE', { parentID: strategicGoalMilestoneID, startDate, endDate }),

    { loadAll: loadAllStrategyProgressCalculationMethods } = entityActionSet(defaults.StrategyProgressCalculationMethod, {
        loadAll: networkAction('LOAD_ALL_STRATEGY_PROGRESS_CALCULATION_METHODS', getAPIMethod('StrategyProgressCalculationMethod', 'loadAll'))
    }),

    { loadAll: loadAllStrategyProgressCalculationSources } = entityActionSet(defaults.StrategyProgressCalculationSource, {
        loadAll: networkAction('LOAD_ALL_STRATEGY_PROGRESS_CALCULATION_SOURCES', getAPIMethod('StrategyProgressCalculationSource', 'loadAll'))
    }),

    { loadAll: loadAllStrategyExecutionSecondaryStatusUpdateTypes } = entityActionSet(defaults.StrategyExecutionSecondaryStatusUpdateType, {
        loadAll: networkAction('LOAD_ALL_STRATEGY_EXECUTION_SECONDARY_STATUS_UPDATE_TYPES', getAPIMethod('StrategyExecutionSecondaryStatusUpdateType', 'loadAll'))
    }),

    { loadAll: loadAllStrategicGoalMilestoneAchievabilities } = entityActionSet(defaults.StrategicGoalMilestoneAchievability, {
        loadAll: networkAction('LOAD_ALL_STRATEGIC_GOAL_MILESTONE_ACHIEVABILITIES', getAPIMethod('StrategicGoalMilestoneAchievability', 'loadAll'))
    }),

    { loadAll: loadAllStrategicGoalMilestoneStatuses } = entityActionSet(defaults.StrategicGoalMilestoneStatus, {
        loadAll: networkAction('LOAD_ALL_STRATEGIC_GOAL_MILESTONE_STATUSES', getAPIMethod('StrategicGoalMilestoneStatus', 'loadAll'))
    }),

    { loadAll: loadAllStrategicGoalMilestoneFinalStatuses } = entityActionSet(defaults.StrategicGoalMilestoneFinalStatus, {
        loadAll: networkAction('LOAD_ALL_STRATEGIC_GOAL_MILESTONE_FINAL_STATUSES', getAPIMethod('StrategicGoalMilestoneFinalStatus', 'loadAll'))
    }),

    strategicGoalMilestoneComments = {
        search: (parentIDs: number[]) =>
            networkAction(
                'SEARCH_STRATEGIC_GOAL_MILESTONE_COMMENTS',
                { parentIDs, templates: parentIDs.map(parentID => ({ MilestoneID: parentID })) },
                getAPIMethod<entities.StrategicGoalMilestoneComment[]>('StrategicGoalMilestoneComment', 'search'),
                parentIDs.map(parentID => ({ MilestoneID: parentID }))
            ),
        loadSlice: (parentID: number, options: { startID?: number; maxCount?: number; slicingOrder?: number; } = {}) =>
            networkAction(
                'LOAD_STRATEGIC_GOAL_MILESTONE_COMMENTS_SLICE',
                { parentID, template: { MilestoneID: parentID } },
                getAPIMethod<entities.NetworkPaginationSlice<entities.StrategicGoalMilestoneComment>>('StrategicGoalMilestoneComment', 'searchSlice'),
                { MilestoneID: parentID },
                options
            ),
        ...entityActionSet(defaults.StrategicGoalMilestoneReport, defaults.StrategicGoalMilestoneComment, {
            add: networkAction('ADD_STRATEGIC_GOAL_MILESTONE_COMMENT', getAPIMethod('StrategicGoalMilestoneComment', 'add')),
            edit: networkAction('EDIT_STRATEGIC_GOAL_MILESTONE_COMMENT', getAPIMethod('StrategicGoalMilestoneComment', 'edit'))
        })
    },

    strategicGoalMilestoneMetrics = {
        search: (parentIDs: number[]) =>
            networkAction(
                'SEARCH_STRATEGIC_GOAL_MILESTONE_METRICS',
                { parentIDs, templates: parentIDs.map(parentID => ({ MilestoneID: parentID })) },
                getAPIMethod<entities.GoalMetricReport[]>('GoalMetric', 'search'),
                parentIDs.map(parentID => ({ MilestoneID: parentID }))
            ),
        loadSlice: (parentID: number, options: { startID?: number; maxCount?: number; slicingOrder?: number; } = {}) =>
            networkAction(
                'LOAD_STRATEGIC_GOAL_MILESTONE_METRIC_SLICE',
                { parentID, template: { MilestoneID: parentID } },
                getAPIMethod<entities.NetworkPaginationSlice<entities.GoalMetricReport>>('GoalMetric', 'searchSlice'),
                { MilestoneID: parentID },
                options
            ),
        ...entityActionSet(defaults.StrategicGoalMilestoneReport, defaults.GoalMetricReport, {
            load: networkAction('LOAD_STRATEGIC_GOAL_MILESTONE_METRIC', getAPIMethod('GoalMetric', 'load')),
            edit: networkAction('EDIT_STRATEGIC_GOAL_MILESTONE_METRIC', getAPIMethod('GoalMetric', 'edit')),
            delete: networkAction('DELETE_STRATEGIC_GOAL_MILESTONE_METRIC', getAPIMethod('GoalMetric', 'delete'))
        }),
        add: (parentID: number, entity: entities.GoalMetricReport) =>
            networkAction(
                'ADD_STRATEGIC_GOAL_MILESTONE_METRIC',
                { parentID, entityID: entity.ID, entity },
                getAPIMethod<entities.GoalMetricReport>('GoalMetric', 'addAndLoad'),
                entity
            )
    },

    strategicGoalMilestoneStatusUpdates = {
        search: (parentIDs: number[]) =>
            networkAction(
                'SEARCH_STRATEGIC_GOAL_MILESTONE_STATUS_UPDATES',
                { parentIDs, templates: parentIDs.map(parentID => ({ MilestoneID: parentID })) },
                getAPIMethod<entities.StrategicGoalMilestoneStatusUpdate[]>('StrategicGoalMilestoneStatusUpdate', 'search'),
                parentIDs.map(parentID => ({ MilestoneID: parentID }))
            ),
        loadSlice: (parentID: number, options: { startID?: number; maxCount?: number; slicingOrder?: number; } = {}) =>
            networkAction(
                'LOAD_STRATEGIC_GOAL_MILESTONE_STATUS_UPDATE_SLICE',
                { parentID, template: { MilestoneID: parentID } },
                getAPIMethod<entities.NetworkPaginationSlice<entities.StrategicGoalMilestoneStatusUpdate>>('StrategicGoalMilestoneStatusUpdate', 'searchSlice'),
                { MilestoneID: parentID },
                options
            ),
        ...entityActionSet(defaults.StrategicGoalMilestoneReport, defaults.StrategicGoalMilestoneStatusUpdate, {
            add: networkAction('ADD_STRATEGIC_GOAL_MILESTONE_STATUS_UPDATE', getAPIMethod('StrategicGoalMilestoneStatusUpdate', 'addAndCreateInsight')),
            edit: networkAction('EDIT_STRATEGIC_GOAL_MILESTONE_STATUS_UPDATE', getAPIMethod('StrategicGoalMilestoneStatusUpdate', 'edit')),
            delete: networkAction('DELETE_STRATEGIC_GOAL_MILESTONE_STATUS_UPDATE', getAPIMethod('StrategicGoalMilestoneStatusUpdate', 'delete'))
        })
    },

    strategicGoalMilestoneSecondaryStatusUpdates = {
        loadSlice: (parentID: number, options: { startID?: number; maxCount?: number; slicingOrder?: number; } = {}) =>
            networkAction(
                'LOAD_STRATEGIC_GOAL_MILESTONE_SECONDARY_STATUS_UPDATE_SLICE',
                { parentID, template: { MilestoneID: parentID } },
                getAPIMethod<entities.NetworkPaginationSlice<entities.StrategicGoalMilestoneSecondaryStatusUpdate>>('StrategicGoalMilestoneSecondaryStatusUpdate', 'searchSlice'),
                { MilestoneID: parentID },
                options
            ),
        ...entityActionSet(defaults.StrategicGoalMilestoneReport, defaults.StrategicGoalMilestoneSecondaryStatusUpdate, {
            add: networkAction('ADD_STRATEGIC_GOAL_MILESTONE_SECONDARY_STATUS_UPDATE', getAPIMethod('StrategicGoalMilestoneSecondaryStatusUpdate', 'add')),
            voidAll: action('VOID_ALL_STRATEGIC_GOAL_MILESTONE_SECONDARY_STATUS_UPDATES')
        })
    },

    strategicGoalMilestoneCustomFieldLinks = entityActionSet(defaults.StrategicGoalMilestoneReport, defaults.StrategyExecutionCustomFieldLink, {
        addAll: networkAction('ADD_ALL_STRATEGIC_GOAL_MILESTONE_CUSTOM_FIELD_LINKS', getAPIMethod('StrategyExecutionCustomFieldLink', 'addAll')),
        editAll: networkAction('EDIT_ALL_STRATEGIC_GOAL_MILESTONE_CUSTOM_FIELD_LINKS', getAPIMethod('StrategyExecutionCustomFieldLink', 'editAll')),
        deleteAll: networkAction('DELETE_ALL_STRATEGIC_GOAL_MILESTONE_CUSTOM_FIELD_LINKS', getAPIMethod('StrategyExecutionCustomFieldLink', 'deleteAll'))
    }),

    strategicGoalMilestoneCustomEntityDatas = entityActionSet(defaults.StrategicGoalMilestoneReport, defaults.StrategyExecutionCustomEntityData, {
        addAll: networkAction('ADD_ALL_STRATEGIC_GOAL_MILESTONE_CUSTOM_ENTITY_DATA', getAPIMethod('StrategyExecutionCustomEntityData', 'addAll')),
        add: networkAction('ADD_STRATEGIC_GOAL_MILESTONE_CUSTOM_ENTITY_DATA', getAPIMethod('StrategyExecutionCustomEntityData', 'add')),
        editAll: networkAction('EDIT_ALL_STRATEGIC_GOAL_MILESTONE_CUSTOM_ENTITY_DATA', getAPIMethod('StrategyExecutionCustomEntityData', 'editAll')),
        edit: networkAction('EDIT_STRATEGIC_GOAL_MILESTONE_CUSTOM_ENTITY_DATA', getAPIMethod('StrategyExecutionCustomEntityData', 'edit')),
        deleteAll: networkAction('DELETE_ALL_STRATEGIC_GOAL_MILESTONE_CUSTOM_ENTITY_DATA', getAPIMethod('StrategyExecutionCustomEntityData', 'deleteAll')),
        delete: networkAction('DELETE_STRATEGIC_GOAL_MILESTONE_CUSTOM_ENTITY_DATA', getAPIMethod('StrategyExecutionCustomEntityData', 'delete'))
    }),

    strategicGoalMilestoneTeamLinks = entityActionSet(defaults.StrategicGoalMilestoneReport, defaults.StrategicGoalMilestoneTeamLink, {
        addAll: networkAction('ADD_ALL_STRATEGIC_GOAL_MILESTONE_TEAM_LINKS', getAPIMethod('StrategicGoalMilestoneTeamLink', 'addAll')),
        deleteAll: networkAction('DELETE_ALL_STRATEGIC_GOAL_MILESTONE_TEAM_LINKS', getAPIMethod('StrategicGoalMilestoneTeamLink', 'deleteAll'))
    }),

    strategicGoalMilestoneSecondaryOwners = entityActionSet(defaults.StrategicGoalMilestoneReport, defaults.StrategicGoalMilestoneSecondaryOwner, {
        addAll: networkAction('ADD_ALL_STRATEGIC_GOAL_MILESTONE_SECONDARY_OWNERS', getAPIMethod('StrategicGoalMilestoneSecondaryOwner', 'addAll')),
        deleteAll: networkAction('DELETE_ALL_STRATEGIC_GOAL_MILESTONE_SECONDARY_OWNERS', getAPIMethod('StrategicGoalMilestoneSecondaryOwner', 'deleteAll'))
    }),

    strategicPrograms = {
        loadAll: (options: { maxCommentsCount?: number; maxStatusUpdatesCount?: number; includeDataActuals?: number } = {}) =>
            networkAction('LOAD_ALL_STRATEGIC_PROGRAMS', getAPIMethod('StrategicProgram', 'loadAll'), options),
        loadAllQuietly: (options: { maxCommentsCount?: number; maxStatusUpdatesCount?: number; } = {}) =>
            networkAction('LOAD_ALL_STRATEGIC_PROGRAMS_QUIETLY', getAPIMethod('StrategicProgram', 'loadAll'), options),
        loadMany: (entityIDs: number[], options: { maxCommentsCount?: number; maxStatusUpdatesCount?: number; } = {}) =>
            networkAction(
                'LOAD_MANY_STRATEGIC_PROGRAMS',
                { entityIDs, ...options },
                getAPIMethod<entities.StrategicProgramReport[]>('StrategicProgram', 'loadMany'),
                entityIDs,
                options
            ),
        load: (entityID: number, { maxCommentsCount, maxStatusUpdatesCount }: { maxCommentsCount: number; maxStatusUpdatesCount: number; } = { maxCommentsCount: 0, maxStatusUpdatesCount: 0 }) =>
            networkAction('LOAD_STRATEGIC_PROGRAM', { entityID }, getAPIMethod('StrategicProgram', 'load'), entityID, { maxCommentsCount, maxStatusUpdatesCount }),
        add: (entity: entities.StrategicProgramReport, sendNotificationToOwner = true) =>
            networkAction('ADD_STRATEGIC_PROGRAM', { entity, sendNotificationToOwner }, getAPIMethod('StrategicProgram', 'add'), entity, { sendNotificationToOwner }),
        ...entityActionSet(defaults.StrategicProgramReport, {
            addAll: networkAction('ADD_ALL_STRATEGIC_PROGRAMS', getAPIMethod('StrategicProgram', 'addAll')),
            editAll: networkAction('EDIT_ALL_STRATEGIC_PROGRAMS', getAPIMethod('StrategicProgram', 'editAll')),
            edit: networkAction('EDIT_STRATEGIC_PROGRAM', getAPIMethod('StrategicProgram', 'edit')),
            delete: networkAction('DELETE_STRATEGIC_PROGRAM', getAPIMethod('StrategicProgram', 'delete')),
            voidAll: action('VOID_ALL_STRATEGIC_PROGRAMS')
        }),
        editAndClose: (entity: entities.StrategicProgramReport) =>
            networkAction(
                'EDIT_AND_CLOSE_STRATEGIC_PROGRAM',
                { entityID: entity.ID, entity },
                getAPIMethod<entities.StrategicProgramReport>('StrategicProgramApi', 'strategicProgramEditAndCloseProgramPut'),
                entity
            )
    },

    { loadAll: loadAllStrategicProgramRiskLevels } = entityActionSet(defaults.StrategicProgramRiskLevel, {
        loadAll: networkAction('LOAD_ALL_STRATEGIC_PROGRAM_RISK_LEVELS', getAPIMethod('StrategicProgramRiskLevel', 'loadAll'))
    }),

    { loadAll: loadAllStrategicProgramStatuses } = entityActionSet(defaults.StrategicProgramStatus, {
        loadAll: networkAction('LOAD_ALL_STRATEGIC_PROGRAM_STATUSES', getAPIMethod('StrategicProgramStatus', 'loadAll'))
    }),

    { loadAll: loadAllStrategicProgramFinalStatuses } = entityActionSet(defaults.StrategicProgramFinalStatus, {
        loadAll: networkAction('LOAD_ALL_STRATEGIC_PROGRAM_FINAL_STATUSES', getAPIMethod('StrategicProgramFinalStatus', 'loadAll'))
    }),

    { loadAll: loadAllStrategicProgramUpdateFrequencies } = entityActionSet(defaults.StrategicProgramUpdateFrequency, {
        loadAll: networkAction('LOAD_ALL_STRATEGIC_PROGRAM_UPDATE_FREQUENCIES', getAPIMethod('StrategicProgramUpdateFrequency', 'loadAll'))
    }),

    strategicProgramSecondaryOwners = entityActionSet(defaults.StrategicProgramReport, defaults.StrategicProgramSecondaryOwner, {
        addAll: networkAction('ADD_ALL_STRATEGIC_PROGRAM_SECONDARY_OWNERS', getAPIMethod('StrategicProgramSecondaryOwner', 'addAll')),
        deleteAll: networkAction('DELETE_ALL_STRATEGIC_PROGRAM_SECONDARY_OWNERS', getAPIMethod('StrategicProgramSecondaryOwner', 'deleteAll'))
    }),

    strategicProgramCustomFieldLinks = entityActionSet(defaults.StrategicProgramReport, defaults.StrategyExecutionCustomFieldLink, {
        addAll: networkAction('ADD_ALL_STRATEGIC_PROGRAM_CUSTOM_FIELD_LINKS', getAPIMethod('StrategyExecutionCustomFieldLink', 'addAll')),
        editAll: networkAction('EDIT_ALL_STRATEGIC_PROGRAM_CUSTOM_FIELD_LINKS', getAPIMethod('StrategyExecutionCustomFieldLink', 'editAll')),
        deleteAll: networkAction('DELETE_ALL_STRATEGIC_PROGRAM_CUSTOM_FIELD_LINKS', getAPIMethod('StrategyExecutionCustomFieldLink', 'deleteAll'))
    }),

    strategicProgramCustomEntityDatas = entityActionSet(defaults.StrategicProgramReport, defaults.StrategyExecutionCustomEntityData, {
        addAll: networkAction('ADD_ALL_STRATEGIC_PROGRAM_CUSTOM_ENTITY_DATA', getAPIMethod('StrategyExecutionCustomEntityData', 'addAll')),
        add: networkAction('ADD_STRATEGIC_PROGRAM_CUSTOM_ENTITY_DATA', getAPIMethod('StrategyExecutionCustomEntityData', 'add')),
        editAll: networkAction('EDIT_ALL_STRATEGIC_PROGRAM_CUSTOM_ENTITY_DATA', getAPIMethod('StrategyExecutionCustomEntityData', 'editAll')),
        edit: networkAction('EDIT_STRATEGIC_PROGRAM_CUSTOM_ENTITY_DATA', getAPIMethod('StrategyExecutionCustomEntityData', 'edit')),
        deleteAll: networkAction('DELETE_ALL_STRATEGIC_PROGRAM_CUSTOM_ENTITY_DATA', getAPIMethod('StrategyExecutionCustomEntityData', 'deleteAll')),
        delete: networkAction('DELETE_STRATEGIC_PROGRAM_CUSTOM_ENTITY_DATA', getAPIMethod('StrategyExecutionCustomEntityData', 'delete'))
    }),

    strategicProgramComments = {
        loadSlice: (parentID: number, options: { startID?: number; maxCount?: number; slicingOrder?: number; } = {}) =>
            networkAction(
                'LOAD_STRATEGIC_PROGRAM_COMMENTS_SLICE',
                { parentID, template: { ProgramID: parentID } },
                getAPIMethod<entities.NetworkPaginationSlice<entities.StrategicProgramComment>>('StrategicProgramComment', 'searchSlice'),
                { ProgramID: parentID },
                options
            ),
        ...entityActionSet(defaults.StrategicProgramReport, defaults.StrategicProgramComment, {
            add: networkAction('ADD_STRATEGIC_PROGRAM_COMMENT', getAPIMethod('StrategicProgramComment', 'add')),
            edit: networkAction('EDIT_STRATEGIC_PROGRAM_COMMENT', getAPIMethod('StrategicProgramComment', 'edit'))
        })
    },

    strategicProgramMetrics = {
        ...entityActionSet(defaults.StrategicProgramReport, defaults.GoalMetricReport, {
            load: networkAction('LOAD_STRATEGIC_PROGRAM_METRIC', getAPIMethod('GoalMetric', 'load')),
            edit: networkAction('EDIT_STRATEGIC_PROGRAM_METRIC', getAPIMethod('GoalMetric', 'edit')),
            delete: networkAction('DELETE_STRATEGIC_PROGRAM_METRIC', getAPIMethod('GoalMetric', 'delete'))
        }),
        add: (parentID: number, entity: entities.GoalMetricReport) =>
            networkAction('ADD_STRATEGIC_PROGRAM_METRIC', { parentID, entityID: entity.ID, entity }, getAPIMethod<entities.GoalMetricReport>('GoalMetricApi', 'goalMetricAddAndLoadPut'), entity)
    },

    strategicProgramStatusUpdates = {
        search: (parentIDs: number[]) =>
            networkAction(
                'SEARCH_STRATEGIC_PROGRAM_STATUS_UPDATES',
                { parentIDs, templates: parentIDs.map(parentID => ({ ProgramID: parentID })) },
                getAPIMethod<entities.StrategicProgramStatusUpdateReport[]>('StrategicProgramStatusUpdate', 'search'),
                parentIDs.map(parentID => ({ ProgramID: parentID }))
            ),
        loadSlice: (parentID: number, options: { startID?: number; maxCount?: number; order?: number; } = {}) =>
            networkAction(
                'LOAD_STRATEGIC_PROGRAM_STATUS_UPDATES_SLICE',
                { parentID, template: { ProgramID: parentID } },
                getAPIMethod<entities.NetworkPaginationSlice<entities.StrategicProgramStatusUpdateReport>>('StrategicProgramStatusUpdate', 'searchSlice'),
                { ProgramID: parentID },
                options
            ),
        add: (parentID: number, entity: entities.StrategicProgramStatusUpdateReport, sync = true, updateIndicatorActualData = true, addInsight = false): AddStrategicProgramStatusUpdate =>
            async(dispatch: any) => {
                if (!sync) {
                    dispatch({
                        type: 'ADD_STRATEGIC_PROGRAM_STATUS_UPDATE',
                        payload: { status: RequestStatus.UNSYNCED, parentID, entity, entityID: entity.ID, response: undefined, updateIndicatorActualData, addInsight }
                    });

                    return null;
                }

                const newID = await dispatch(networkAction(
                    'ADD_STRATEGIC_PROGRAM_STATUS_UPDATE',
                    { parentID, entityID: entity.ID, entity, updateIndicatorActualData, addInsight },
                    getAPIMethod<entities.StrategicProgramStatusUpdateReport>('StrategicProgramStatusUpdate', 'add'),
                    entity,
                    { updateIndicatorActualData, addInsight }
                ));

                return newID;
            },
        ...entityActionSet(defaults.StrategicProgramReport, defaults.StrategicProgramStatusUpdateReport, {
            edit: networkAction('EDIT_STRATEGIC_PROGRAM_STATUS_UPDATE', getAPIMethod('StrategicProgramStatusUpdate', 'edit')),
            delete: networkAction('DELETE_STRATEGIC_PROGRAM_STATUS_UPDATE', getAPIMethod('StrategicProgramStatusUpdate', 'delete'))
        })
    },

    strategicProgramStatusUpdateRecipients = {
        ...entityActionSet(defaults.StrategicProgramReport, defaults.StrategicProgramStatusUpdateReport, defaults.StrategicProgramStatusUpdateRecipient, {
            addAll: networkAction('ADD_ALL_STRATEGIC_PROGRAM_STATUS_UPDATE_RECIPIENTS', getAPIMethod('StrategicProgramStatusUpdateRecipient', 'addAll'))
        }),
        loadSlice: (grandParentID: number, parentID: number, options: { startID?: number; maxCount?: number; order?: number; } = {}) =>
            networkAction(
                'LOAD_STRATEGIC_PROGRAM_STATUS_UPDATE_RECIPIENTS_SLICE',
                { grandParentID, parentID, template: { StatusUpdateID: parentID } },
                getAPIMethod<entities.NetworkPaginationSlice<entities.StrategicProgramStatusUpdateRecipient>>('StrategicProgramStatusUpdateRecipient', 'searchSlice'),
                { StatusUpdateID: parentID },
                options
            )
    },

    strategyFreeNodes = entityActionSet(defaults.StrategyFreeNodeReport, {
        loadAll: networkAction('LOAD_ALL_STRATEGY_FREE_NODES', getAPIMethod('StrategyFreeNode', 'loadAll')),
        load: networkAction('LOAD_STRATEGY_FREE_NODE', getAPIMethod('StrategyFreeNode', 'load')),
        add: networkAction('ADD_STRATEGY_FREE_NODE', getAPIMethod('StrategyFreeNode', 'add')),
        edit: networkAction('EDIT_STRATEGY_FREE_NODE', getAPIMethod('StrategyFreeNode', 'edit')),
        delete: networkAction('DELETE_STRATEGY_FREE_NODE', getAPIMethod('StrategyFreeNode', 'delete')),
        voidAll: action('VOID_ALL_STRATEGY_FREE_NODES')
    }),

    setStrategyFilter = (filter: Partial<StrategyFilter>) => action('SET_STRATEGY_FILTER', { filter }),
    setStrategyExecutionFilter = (filter: Partial<StrategyFilter>) => action('SET_STRATEGY_EXECUTION_FILTER', { filter }),
    setStrategyContentSettings = (settings: StrategyContentSettings, dashboardPageID?: number, dashboardBlockID?: number) =>
        action('SET_STRATEGY_CONTENT_SETTINGS', { settings, dashboardPageID, dashboardBlockID }),
    setStrategyActionListContentSettings = (settings: StrategyActionListContentSettings) => action('SET_STRATEGY_ACTION_LIST_CONTENT_SETTINGS', { settings }),
    setStrategyPlannerFilter = (filter: StrategyPlannerFilter) => action('SET_STRATEGY_PLANNER_FILTER', { filter }),
    setStrategyPlannerExpandedNodes = (expandedNodes: string[]) => action('SET_STRATEGY_PLANNER_EXPANDED_NODES', { expandedNodes }),
    setStrategyOrganizationStructureFilter = (filter: StrategyOrganizationStructureFilter) => action('SET_STRATEGY_ORGANIZATION_STRUCTURE_FILTER', { filter }),

    setStrategyTimelineCollapsedGoals = (goalIDs: number[]) => action('SET_STRATEGY_TIMELINE_COLLAPSED_GOALS', { goalIDs }),

    { loadAll: loadAllStrategyPublishingLevels } = entityActionSet(defaults.StrategyPublishingLevel, {
        loadAll: networkAction('LOAD_ALL_STRATEGY_PUBLISHING_LEVELS', getAPIMethod('StrategyPublishingLevel', 'loadAll'))
    }),

    { loadAll: loadAllStrategyExecutionTypes } = entityActionSet(defaults.StrategyExecutionType, {
        loadAll: networkAction('LOAD_ALL_STRATEGY_EXECUTION_TYPES', getAPIMethod('StrategyExecutionType', 'loadAll'))
    }),

    businessReviewSchedules = entityActionSet(defaults.BusinessReviewSchedule, {
        loadAll: networkAction('LOAD_ALL_BUSINESS_REVIEW_SCHEDULES', getAPIMethod('BusinessReviewSchedule', 'loadAll')),
        addAll: networkAction('ADD_ALL_BUSINESS_REVIEW_SCHEDULES', getAPIMethod('BusinessReviewSchedule', 'addAll'))
    }),

    businessReviewTemplates = {
        loadAll: (options: { maxSessionsCount?: number; } = {}) =>
            networkAction('LOAD_ALL_BUSINESS_REVIEW_TEMPLATES', getAPIMethod('BusinessReviewTemplate', 'loadAll'), options),
        ...entityActionSet(defaults.BusinessReviewTemplateReport, {
            add: networkAction('ADD_BUSINESS_REVIEW_TEMPLATE', getAPIMethod('BusinessReviewTemplate', 'add'))
        }),
        generateSessionsForUser: (targetUserID: number, reloadAfterSync = false): any =>
            async(dispatch: any) => {
                const response = await dispatch(networkAction(
                    'GENERATE_BUSINESS_REVIEW_SESSIONS_FOR_USER',
                    { targetUserID },
                    getAPIMethod<entities.BusinessReviewTemplateReport>('BusinessReviewTemplate', 'generateSessionsForUser'),
                    targetUserID
                ));

                if (reloadAfterSync) {
                    return dispatch(businessReviewTemplates.loadAll({ maxSessionsCount: 0 }));
                }

                return response;
            }
    },

    businessReviewSessions = {
        ...entityActionSet(defaults.BusinessReviewTemplateReport, defaults.BusinessReviewSession, {
            load: networkAction('LOAD_BUSINESS_REVIEW_SESSION', getAPIMethod('BusinessReviewSession', 'load')),
            edit: networkAction('EDIT_BUSINESS_REVIEW_SESSION', getAPIMethod('BusinessReviewSession', 'edit'))
        }),
        searchSlice: (parentID: number, options: { startID?: number; maxCount?: number; order?: number; } = {}) =>
            networkAction(
                'SEARCH_BUSINESS_REVIEW_SESSIONS_SLICE', { parentID, template: { TemplateID: parentID } }, getAPIMethod<entities.NetworkPaginationSlice<entities.BusinessReviewSession>>('BusinessReviewSessionApi', 'businessReviewSessionSearchSlicePut'), { TemplateID: parentID }, options
            ),
        finishSession: (parentID: number, entityID: number, endStatusID: number, comment?: string, reloadAfterSync = false): any =>
            async(dispatch: any) => {
                const response = await dispatch(networkAction(
                    'FINISH_BUSINESS_REVIEW_SESSION',
                    { entityID, endStatusID, comment },
                    getAPIMethod<entities.BusinessReviewSession>('BusinessReviewSession', 'finishSession'),
                    entityID,
                    endStatusID,
                    { comment }
                ));

                if (reloadAfterSync) {
                    return dispatch(businessReviewSessions.load(parentID, entityID));
                }

                return response;
            }
    },

    businessReviewSteps = entityActionSet(defaults.BusinessReviewTemplateReport, defaults.BusinessReviewStep, {
        add: networkAction('ADD_BUSINESS_REVIEW_STEP', getAPIMethod('BusinessReviewStep', 'add')),
        edit: networkAction('EDIT_BUSINESS_REVIEW_STEP', getAPIMethod('BusinessReviewStep', 'edit')),
        delete: networkAction('DELETE_BUSINESS_REVIEW_STEP', getAPIMethod('BusinessReviewStep', 'delete'))
    }),

    { loadAll: loadAllBusinessReviewSessionStatuses } = entityActionSet(defaults.BusinessReviewSessionStatus, {
        loadAll: networkAction('LOAD_ALL_BUSINESS_REVIEW_SESSION_STATUSES', getAPIMethod('BusinessReviewSessionStatus', 'loadAll'))
    }),

    businessReviewDefaultUserSettings = {
        load: () =>
            networkAction('LOAD_BUSINESS_REVIEW_DEFAULT_USER_SETTINGS', getAPIMethod<entities.BusinessReviewUserSettings>('BusinessReviewUserSettings', 'loadDefaultUserSettings')),
        ...entityActionSet(defaults.BusinessReviewUserSettings, {
            edit: networkAction('EDIT_BUSINESS_REVIEW_DEFAULT_USER_SETTINGS', getAPIMethod('BusinessReviewUserSettings', 'edit')),
            voidAll: action('VOID_ALL_BUSINESS_REVIEW_DEFAULT_USER_SETTINGS')
        })
    },

    businessReviewUserSettings = {
        loadUserSettings: () =>
            networkAction('LOAD_BUSINESS_REVIEW_USER_SETTINGS', getAPIMethod<entities.BusinessReviewUserSettings[]>('BusinessReviewUserSettings', 'loadUserSettings')),
        mergeAll: (entities: entities.BusinessReviewUserSettings[]) =>
            networkAction('MERGE_ALL_BUSINESS_REVIEW_USER_SETTINGS', { entities }, getAPIMethod<number[]>('BusinessReviewUserSettings', 'mergeAll'), entities),
        ...entityActionSet(defaults.BusinessReviewUserSettings, {
            add: networkAction('ADD_BUSINESS_REVIEW_USER_SETTINGS', getAPIMethod('BusinessReviewUserSettings', 'add')),
            edit: networkAction('EDIT_BUSINESS_REVIEW_USER_SETTINGS', getAPIMethod('BusinessReviewUserSettings', 'edit')),
            voidAll: action('VOID_ALL_BUSINESS_REVIEW_USER_SETTINGS')
        })
    },

    { loadAll: loadAllBusinessReviewSectionTypes } = entityActionSet(defaults.BusinessReviewSectionType, {
        loadAll: networkAction('LOAD_ALL_BUSINESS_REVIEW_SECTION_TYPES', getAPIMethod('BusinessReviewSectionType', 'loadAll'))
    }),

    businessReviewDefaultExcludedSections = {
        ...entityActionSet(defaults.BusinessReviewExcludedSection, {
            loadAll: networkAction('LOAD_ALL_BUSINESS_REVIEW_DEFAULT_EXCLUDED_SECTIONS', getAPIMethod('BusinessReviewExcludedSection', 'loadDefaultExcludedSections'))
        }),
        mergeAll: (entities: entities.BusinessReviewExcludedSection[]) =>
            networkAction('MERGE_ALL_BUSINESS_REVIEW_DEFAULT_EXCLUDED_SECTIONS', { entities }, getAPIMethod<number[]>('BusinessReviewExcludedSection', 'mergeAll'), entities)
    },

    businessReviewExcludedSections = {
        loadUserExcludedSections: () =>
            networkAction(
                'LOAD_USER_BUSINESS_REVIEW_EXCLUDED_SECTIONS', getAPIMethod<entities.BusinessReviewExcludedSection[]>('BusinessReviewExcludedSection', 'loadUserExcludedSections')
            ),
        mergeAll: (entities: entities.BusinessReviewExcludedSection[]) =>
            networkAction('MERGE_ALL_BUSINESS_REVIEW_EXCLUDED_SECTIONS', { entities }, getAPIMethod<number[]>('BusinessReviewExcludedSection', 'mergeAll'), entities),
        ...entityActionSet(defaults.BusinessReviewExcludedSection, {
            add: networkAction('ADD_BUSINESS_REVIEW_EXCLUDED_SECTION', getAPIMethod('BusinessReviewExcludedSection', 'add')),
            edit: networkAction('EDIT_BUSINESS_REVIEW_EXCLUDED_SECTION', getAPIMethod('BusinessReviewExcludedSection', 'edit'))
        })
    },

    setBusinessReviewSettings = (settings: BusinessReviewSettings) => action('SET_BUSINESS_REVIEW_SETTINGS', { settings }),

    strategyExecutionCustomFields = entityActionSet(defaults.StrategyExecutionCustomFieldReport, {
        loadAll: networkAction('LOAD_ALL_STRATEGY_EXECUTION_CUSTOM_FIELDS', getAPIMethod('StrategyExecutionCustomField', 'loadAll')),
        load: networkAction('LOAD_STRATEGY_EXECUTION_CUSTOM_FIELD', getAPIMethod('StrategyExecutionCustomField', 'load')),
        addAll: networkAction('ADD_ALL_STRATEGY_EXECUTION_CUSTOM_FIELDS', getAPIMethod('StrategyExecutionCustomField', 'addAll')),
        editAll: networkAction('EDIT_ALL_STRATEGY_EXECUTION_CUSTOM_FIELDS', getAPIMethod('StrategyExecutionCustomField', 'editAll')),
        deleteAll: networkAction('DELETE_ALL_STRATEGY_EXECUTION_CUSTOM_FIELDS', getAPIMethod('StrategyExecutionCustomField', 'deleteAll')),
        delete: networkAction('DELETE_STRATEGY_EXECUTION_CUSTOM_FIELD', getAPIMethod('StrategyExecutionCustomField', 'delete')),
        voidAll: action('DELETE_ALL_STRATEGY_EXECUTION_CUSTOM_FIELDS')
    }),

    strategyExecutionCustomFieldOptions = entityActionSet(defaults.StrategyExecutionCustomFieldReport, defaults.StrategyExecutionCustomFieldOption, {
        addAll: networkAction('ADD_ALL_STRATEGY_EXECUTION_CUSTOM_FIELD_OPTIONS', getAPIMethod('StrategyExecutionCustomFieldOption', 'addAll')),
        editAll: networkAction('EDIT_ALL_STRATEGY_EXECUTION_CUSTOM_FIELD_OPTIONS', getAPIMethod('StrategyExecutionCustomFieldOption', 'editAll')),
        deleteAll: networkAction('DELETE_ALL_STRATEGY_EXECUTION_CUSTOM_FIELD_OPTIONS', getAPIMethod('StrategyExecutionCustomFieldOption', 'deleteAll'))
    }),

    { loadAll: loadAllStrategyExecutionCustomFieldTypes } = entityActionSet(defaults.StrategyExecutionCustomFieldType, {
        loadAll: networkAction('LOAD_ALL_STRATEGY_EXECUTION_CUSTOM_FIELD_TYPES', getAPIMethod('StrategyExecutionCustomFieldType', 'loadAll'))
    }),

    strategyExecutionCustomEntities = entityActionSet(defaults.StrategyExecutionCustomEntity, {
        loadAll: networkAction('LOAD_ALL_STRATEGY_EXECUTION_CUSTOM_ENTITIES', getAPIMethod('StrategyExecutionCustomEntity', 'loadAll'))
    }),

    strategySavedViews = entityActionSet(defaults.StrategySavedView, {
        loadAll: networkAction('LOAD_ALL_STRATEGY_SAVED_VIEWS', getAPIMethod('StrategySavedView', 'loadAllPersonalViews')),
        add: networkAction('ADD_STRATEGY_SAVED_VIEW', getAPIMethod('StrategySavedView', 'add')),
        edit: networkAction('EDIT_STRATEGY_SAVED_VIEW', getAPIMethod('StrategySavedView', 'edit')),
        delete: networkAction('DELETE_STRATEGY_SAVED_VIEW', getAPIMethod('StrategySavedView', 'delete'))
    });

export type StrategyAction = ExtractAction<
| typeof businessModelCanvasAreas
| typeof businessModels
| typeof businessModelCanvasItems
| typeof loadAllBusinessModelCanvasItemTypes
| typeof loadAllStrategyHealthReports
| typeof strategyProcesses
| typeof strategyProcessThemes
| typeof loadAllStrategyProcessSubthemeTypes
| typeof strategyProcessSubthemes
| typeof strategyProcessSubthemeLinks
| typeof strategyProcessPhases
| typeof strategyProcessPhaseContents
| typeof strategyTaxonomies
| typeof strategyDescriptions
| typeof loadAllStrategyDescriptionStatuses
| typeof loadAllStrategyPublishingTypes
| typeof loadAllStrategyNamingTypes
| typeof strategyDescriptionParticipators
| typeof strategyDescriptionAdmins
| typeof strategyMetrics
| typeof strategicGoals
| typeof setGoalCustomJourneyRange
| typeof criticalSuccessFactors
| typeof goalMetrics
| typeof aggregateGoalMetric
| typeof loadAllGoalMetricValueTypes
| typeof loadAllGoalMetricValueSources
| typeof strategicGoalMilestones
| typeof setMilestoneCustomRoadmapRange
| typeof loadAllStrategyProgressCalculationMethods
| typeof loadAllStrategyProgressCalculationSources
| typeof loadAllStrategyExecutionSecondaryStatusUpdateTypes
| typeof loadAllStrategicGoalMilestoneAchievabilities
| typeof loadAllStrategicGoalMilestoneStatuses
| typeof loadAllStrategicGoalMilestoneFinalStatuses
| typeof strategicGoalMilestoneComments
| typeof strategicGoalMilestoneMetrics
| typeof strategicGoalMilestoneStatusUpdates
| typeof strategicGoalMilestoneSecondaryStatusUpdates
| typeof strategicGoalMilestoneCustomFieldLinks
| typeof strategicGoalMilestoneCustomEntityDatas
| typeof strategicGoalMilestoneTeamLinks
| typeof strategicGoalMilestoneSecondaryOwners
| typeof strategicPrograms
| typeof loadAllStrategicProgramRiskLevels
| typeof loadAllStrategicProgramStatuses
| typeof loadAllStrategicProgramFinalStatuses
| typeof loadAllStrategicProgramUpdateFrequencies
| typeof strategicProgramSecondaryOwners
| typeof strategicProgramCustomFieldLinks
| typeof strategicProgramCustomEntityDatas
| typeof strategicProgramComments
| typeof strategicProgramMetrics
| typeof strategicProgramStatusUpdates
| typeof strategicProgramStatusUpdateRecipients
| typeof strategyFreeNodes
| typeof setStrategyFilter
| typeof setStrategyContentSettings
| typeof setStrategyActionListContentSettings
| typeof setStrategyPlannerExpandedNodes
| typeof setStrategyPlannerFilter
| typeof setStrategyTimelineCollapsedGoals
| typeof loadAllStrategyPublishingLevels
| typeof loadAllStrategyExecutionTypes
| typeof businessReviewSchedules
| typeof businessReviewTemplates
| typeof businessReviewSessions
| typeof businessReviewSteps
| typeof loadAllBusinessReviewSessionStatuses
| typeof businessReviewDefaultUserSettings
| typeof businessReviewUserSettings
| typeof loadAllBusinessReviewSectionTypes
| typeof businessReviewDefaultExcludedSections
| typeof businessReviewExcludedSections
| typeof setBusinessReviewSettings
| typeof strategyExecutionCustomFields
| typeof strategyExecutionCustomFieldOptions
| typeof loadAllStrategyExecutionCustomFieldTypes
| typeof strategyExecutionCustomEntities
| typeof strategySavedViews
>;
